import { action, computed, flow, makeObservable, observable } from 'mobx';
import { computedFn } from 'mobx-utils';
import { getAutocompletedRigOperations } from 'src/api/new-well/requests';
import { BaseApiError, Validation400ApiError, ValidationError } from 'src/errors';
import { assert } from 'src/shared/utils/assert';
import { hasValue } from 'src/shared/utils/common';
import { addDaysToDate, convertSecondsToHours, substractDays, convertHoursToSeconds } from 'src/shared/utils/date';
import { equalsDeep } from 'src/shared/utils/equals-deep';
import { EventBus } from 'src/shared/utils/event-bus';
import { isObjectWithKeys } from 'src/shared/utils/is-object-with-keys';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import { RootStore } from 'src/store';
import { Directories } from 'src/store/directories/directories.store';
import { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import { calculateCommercialSpeed, calculateDurationByCommercialSpeed } from '../../utils';
import { FormDirectoryValuesService } from '../form-directory-values-service';
import { FormStore, IFormEvents } from '../form.entity';
import { GeologicalLoadService } from '../geological-load/geological-load-service';

import { RIG_OPERATION_INDEX_KEY, RIG_OPERATION_STAGES_KEY, RIG_OPERATIONS_KEY } from './consts';
import { RestoreWorkStagesToDirectoryValuesService } from './restore-work-stages-to-directory-values-service';
import { RigOperationSection, SECTION_DATA_FIELDS, TSectionData } from './rig-operation-section.entity';
import { RigOperationStage, STAGE_DATA_FIELDS, TStageData } from './rig-operation-stage.entity';
import { RIG_OPERATION_DATA_FIELDS, RigOperation, TRigOperationData } from './rig-operation.entity';
import { RigOperationsValidation } from './rig-operations-validation';
import { TRawRigOperation, TRawRigOperationSection, TRawRigOperationStage } from './types';

type TValueChangePayload = {
  rigOperation: RigOperation;
  stage?: RigOperationStage;
  section?: RigOperationSection;
};

export const isRigOperationField = (fieldId: string): fieldId is keyof TRigOperationData => {
  return RIG_OPERATION_DATA_FIELDS.includes(fieldId);
};

export const isStageField = (fieldId: string): fieldId is keyof TStageData => {
  return STAGE_DATA_FIELDS.includes(fieldId);
};

export const isSectionField = (fieldId: string): fieldId is keyof TSectionData => {
  return SECTION_DATA_FIELDS.includes(fieldId);
};

const parseObjectWithFaultyValues = (obj: Record<string, unknown>): Record<string, unknown> => {
  const finalObject: Record<string, unknown> = {};

  for (const key in obj) {
    const rawValue = obj[key];

    if (Array.isArray(rawValue) || (typeof rawValue === 'object' && rawValue !== null)) {
      finalObject[key] = rawValue;
      continue;
    }

    finalObject[key] = parsePrimitivesFromString(rawValue);
  }

  return finalObject;
};

const isRawRigOperation = (rawRigOperation: unknown): rawRigOperation is TRawRigOperation => {
  return !!rawRigOperation && typeof rawRigOperation === 'object';
};

export class RigOperationsService {
  private readonly eventBus: EventBus<IFormEvents>;
  private readonly rootStore: RootStore;
  private readonly directoryValuesService: FormDirectoryValuesService;
  private readonly directories: Directories;
  private readonly initialValues: Record<string, unknown> | null;
  private readonly notificationsService: NotificationsStore;
  private readonly formStore: FormStore;
  private readonly restoreWorkStagesToDirectoryValuesService: RestoreWorkStagesToDirectoryValuesService;
  readonly validationService: RigOperationsValidation;
  @observable rigOperations: RigOperation[] = [];
  readonly rigOperationsMap = observable.map<number, RigOperation>();

  constructor(
    rootStore: RootStore,
    formStore: FormStore,
    directoryValuesService: FormDirectoryValuesService,
    geoLoadService: GeologicalLoadService,
    eventBus: EventBus<IFormEvents>,
    initialValues: Record<string, unknown> | null,
  ) {
    this.eventBus = eventBus;
    this.rootStore = rootStore;
    this.formStore = formStore;
    this.notificationsService = rootStore.notifications;
    this.directories = rootStore.directories;
    this.directoryValuesService = directoryValuesService;
    this.initialValues = initialValues ?? null;
    this.validationService = new RigOperationsValidation(rootStore.notifications, formStore, this, geoLoadService);
    this.restoreWorkStagesToDirectoryValuesService = new RestoreWorkStagesToDirectoryValuesService(
      formStore,
      this,
      rootStore.directories,
      rootStore.notifications,
    );

    makeObservable(this);
  }

  private deserializeRigOperation(rawValues: unknown): TRawRigOperation | null {
    if (!isObjectWithKeys(rawValues)) {
      console.error('rawRigOperation is not an object', rawValues);
      return null;
    }

    const rawRigOperationData = parseObjectWithFaultyValues(rawValues);

    for (const key in rawRigOperationData) {
      rawRigOperationData[key] = parsePrimitivesFromString(rawRigOperationData[key]);
    }

    if (!isRawRigOperation(rawRigOperationData)) {
      console.error('rawRigOperationData is invalid', rawRigOperationData);
      return null;
    }

    return rawRigOperationData;
  }

  @action.bound
  private updateRigOperationData(rigOperation: RigOperation, rawRigOperationData: TRawRigOperation): RigOperation {
    rigOperation.updateData(rawRigOperationData);

    if (rawRigOperationData[RIG_OPERATION_STAGES_KEY]?.length) {
      this.replaceRigOperationStages(rigOperation, rawRigOperationData[RIG_OPERATION_STAGES_KEY]);
    }

    return rigOperation;
  }

  @action.bound
  private initializeRigOperations(rawRigOperations: unknown): void {
    if (!rawRigOperations || !Array.isArray(rawRigOperations) || !rawRigOperations.length) {
      return;
    }

    this.rigOperationsMap.clear();
    const rigOperations: RigOperation[] = [];

    for (const rawRigOperation of rawRigOperations) {
      const rawRigOperationData = this.deserializeRigOperation(rawRigOperation);

      if (!rawRigOperationData) {
        console.error('wrong rawRigOperationData');
        return;
      }

      const rigOperation = new RigOperation(rawRigOperationData[RIG_OPERATION_INDEX_KEY], rawRigOperationData);

      if (rawRigOperationData[RIG_OPERATION_STAGES_KEY]?.length) {
        this.initializeRigOperationStages(rigOperation, rawRigOperationData[RIG_OPERATION_STAGES_KEY]);
      }

      rigOperations.push(rigOperation);
      this.rigOperationsMap.set(rigOperation.entityId, rigOperation);
    }

    this.rigOperations = rigOperations;
    this.eventBus.publish('initializedRigOperaionList', rigOperations);
  }

  private initializeRigOperationStages(rigOperation: RigOperation, rawStages: TRawRigOperationStage[]) {
    const stages: RigOperationStage[] = [];

    for (const rawStage of rawStages) {
      // TODO: валидация данных
      const stage = new RigOperationStage(rawStage.index, rawStage);

      if (rawStage.sections?.length) {
        this.initializeRigOperationSections(rigOperation, stage, rawStage.sections);
      }

      stages.push(stage);
    }

    rigOperation.setStages(stages);
    this.eventBus.publish('initializedStageList', { rigOperation, stages });
  }

  private replaceRigOperationStages(rigOperation: RigOperation, rawStages: TRawRigOperationStage[]) {
    const stages: RigOperationStage[] = [];

    for (const rawStage of rawStages) {
      // TODO: валидация данных
      const stage = new RigOperationStage(rawStage.index, rawStage);

      if (rawStage.sections?.length) {
        this.initializeRigOperationSections(rigOperation, stage, rawStage.sections);
      }

      stages.push(stage);
    }

    rigOperation.setStages(stages);
    this.eventBus.publish('updatedStageList', { rigOperation, stages });
  }

  private initializeRigOperationSections(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    rawSections: TRawRigOperationSection[],
  ): void {
    const sections: RigOperationSection[] = [];

    for (const rawSection of rawSections) {
      // TODO: валидация данных
      const section = new RigOperationSection(rawSection.index, rawSection);

      sections.push(section);
    }

    stage.setSections(sections);
    this.eventBus.publish('initializedSectionsList', { rigOperation, stage, sections });
  }

  private replaceRigOperationSections(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    rawSections: TRawRigOperationSection[],
  ): void {
    const sections: RigOperationSection[] = [];

    for (const rawSection of rawSections) {
      // TODO: валидация данных
      const section = new RigOperationSection(rawSection.index, rawSection);

      sections.push(section);
    }

    stage.setSections(sections);
    this.eventBus.publish('updatedSectionsList', { rigOperation, stage, sections });
  }

  private setBrigadeIdByRigId(rigOperation: RigOperation): void {
    const rigsDirectory = this.directories.getObject('Common_Rig');
    const brigadesDirectory = this.directories.getObject('Common_Brigade');

    const rig = rigsDirectory?.find((rig) => rig.id === rigOperation.rigId);
    const brigadeId = brigadesDirectory?.find((brigade) => brigade.id === rig?.data['brigadeId'])?.id;

    if (!hasValue(brigadeId)) {
      return;
    }

    this.onValueChange('brigadeId', brigadeId, { rigOperation });
  }

  private onRigOperationFieldValueChange(fieldId: string, value: unknown, rigOperation: RigOperation): void {
    if (!isRigOperationField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return;
    }

    if (value === rigOperation.data[fieldId]) {
      return;
    }

    rigOperation.clearErrors();

    switch (fieldId) {
      case 'rigId': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        rigOperation.setRigId(value);
        rigOperation.removeError('rigId');

        if (hasValue(value)) {
          this.setBrigadeIdByRigId(rigOperation);
        }

        break;
      }
      case 'brigadeId': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        rigOperation.setBrigadeId(value);
        rigOperation.removeError('brigadeId');

        break;
      }
    }

    this.eventBus.publish('rigOperationItemFieldValueChange', { fieldId, value, rigOperation });
  }

  private onStageFieldValueChange(
    fieldId: string,
    value: unknown,
    rigOperation: RigOperation,
    stage: RigOperationStage,
  ): void {
    if (!isStageField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return;
    }

    if (value === stage.data[fieldId]) {
      return;
    }

    switch (fieldId) {
      case 'startDate': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setStartDate(value);
        stage.removeError('startDate');

        if (!hasValue(value)) {
          break;
        }

        if (hasValue(stage.duration)) {
          const endDate = addDaysToDate(value, stage.duration);

          this.onValueChange('endDate', endDate, { rigOperation, stage });
        } else if (!hasValue(stage.duration) && hasValue(stage.endDate)) {
          const duration = Math.round((convertSecondsToHours(stage.endDate - value) / 24) * 10000) / 10000;

          this.onValueChange('duration', duration, { rigOperation, stage });
        }

        break;
      }
      case 'endDate': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setEndDate(value);
        stage.removeError('endDate');

        if (!hasValue(value)) {
          break;
        }

        if (hasValue(stage.duration) && !hasValue(stage.startDate)) {
          const startDate = substractDays(value, stage.duration);

          this.onValueChange('startDate', startDate, { rigOperation, stage });
        } else if (hasValue(stage.startDate)) {
          const duration = Math.round((convertSecondsToHours(value - stage.startDate) / 24) * 10000) / 10000;

          this.onValueChange('duration', duration, { rigOperation, stage });
        }

        break;
      }
      case 'duration': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setDuration(value);
        stage.removeError('duration');

        if (!hasValue(value) || value < 0 || (!hasValue(stage.startDate) && !hasValue(stage.endDate))) {
          break;
        }

        if (!hasValue(stage.startDate) && hasValue(stage.endDate)) {
          const startDate = stage.endDate - convertHoursToSeconds(value * 24);

          this.onValueChange('startDate', startDate, { rigOperation, stage });
        } else if (hasValue(stage.startDate)) {
          const endDate = addDaysToDate(stage.startDate, value);

          this.onValueChange('endDate', endDate, { rigOperation, stage });
        }

        break;
      }
      case 'wellLifeCycleStageId': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setWellLifeCycleStageId(value);
        stage.removeError('wellLifeCycleStageId');

        if (!hasValue(value)) {
          this.setSections(rigOperation, stage, []);
          this.onValueChange('startDate', null, { rigOperation, stage });
          this.onValueChange('endDate', null, { rigOperation, stage });
          this.onValueChange('duration', null, { rigOperation, stage });
          this.onValueChange('passingValue', null, { rigOperation, stage });
          this.onValueChange('commercialSpeed', null, { rigOperation, stage });
          this.onValueChange('distance', null, { rigOperation, stage });
        } else {
          if (!this.hasSections(value)) {
            this.setSections(rigOperation, stage, []);
          }

          if (!this.hasDistance(value)) {
            this.onValueChange('distance', null, { rigOperation, stage });
          }

          if (!this.isDrillingStage(value)) {
            this.onValueChange('passingValue', null, { rigOperation, stage });
            this.onValueChange('commercialSpeed', null, { rigOperation, stage });
          }
        }

        break;
      }
      case 'passingValue': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setPassingValue(value);
        stage.removeError('passingValue');

        break;
      }
      case 'commercialSpeed': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setCommercialSpeed(value);
        stage.removeError('commercialSpeed');

        break;
      }
      case 'distance': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        stage.setDistance(value);
        stage.removeError('distance');
      }
    }

    this.eventBus.publish('rigOperationItemFieldValueChange', { fieldId, value, rigOperation, stage });
  }

  private onSectionFieldValueChange(
    fieldId: string,
    value: unknown,
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): void {
    if (!isSectionField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return;
    }

    if (value === section.data[fieldId]) {
      return;
    }

    switch (fieldId) {
      case 'startDate': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setStartDate(value);
        section.removeError('startDate');

        if (!hasValue(value)) {
          break;
        }

        if (hasValue(section.duration)) {
          const endDate = addDaysToDate(value, section.duration);

          this.onValueChange('endDate', endDate, { rigOperation, stage, section });
        } else if (!hasValue(section.duration) && hasValue(section.endDate)) {
          const duration = Math.round((convertSecondsToHours(section.endDate - value) / 24) * 10000) / 10000;

          this.onValueChange('duration', duration, { rigOperation, stage, section });
        }

        break;
      }
      case 'endDate': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setEndDate(value);
        section.removeError('endDate');

        if (!hasValue(value)) {
          break;
        }

        if (hasValue(section.duration) && !hasValue(section.startDate)) {
          const startDate = substractDays(value, section.duration);

          this.onValueChange('startDate', startDate, { rigOperation, stage, section });
        } else if (hasValue(section.startDate)) {
          const duration = Math.round((convertSecondsToHours(value - section.startDate) / 24) * 10000) / 10000;

          this.onValueChange('duration', duration, { rigOperation, stage, section });
        }

        break;
      }
      case 'duration': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setDuration(value);
        section.removeError('duration');

        if (!hasValue(value) || value < 0 || (!hasValue(section.startDate) && !hasValue(section.endDate))) {
          break;
        }

        if (!hasValue(section.startDate) && hasValue(section.endDate)) {
          const startDate = section.endDate - convertHoursToSeconds(value * 24);

          this.onValueChange('startDate', startDate, { rigOperation, stage, section });
        } else if (hasValue(section.startDate)) {
          const endDate = addDaysToDate(section.startDate, value);

          this.onValueChange('endDate', endDate, { rigOperation, stage, section });
        }

        break;
      }
      case 'sectionTypeId': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setSectionTypeId(value);
        section.removeError('sectionTypeId');

        break;
      }
      case 'passingValue': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setPassingValue(value);
        section.removeError('passingValue');

        if (
          !hasValue(value) ||
          value < 0 ||
          (!hasValue(section.passingValueMin) && !hasValue(section.passingValueMax))
        ) {
          break;
        }

        if (!hasValue(section.passingValueMin) && hasValue(section.passingValueMax)) {
          const passingValueMin = section.passingValueMax - value;

          this.onValueChange('passingValueMin', passingValueMin, { rigOperation, stage, section });
        } else if (hasValue(section.passingValueMin)) {
          const passingValueMax = section.passingValueMin + value;

          this.onValueChange('passingValueMax', passingValueMax, { rigOperation, stage, section });
        }

        break;
      }

      case 'passingValueMin': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setPassingValueMin(value);
        section.removeError('passingValueMin');

        if (!hasValue(value) || (hasValue(section.passingValueMax) && value > section.passingValueMax)) {
          break;
        }

        if (hasValue(section.passingValue) && !hasValue(section.passingValueMax)) {
          const passingValueMax = value + section.passingValue;

          this.onValueChange('passingValueMax', passingValueMax, { rigOperation, stage, section });
        } else if (hasValue(section.passingValueMax)) {
          const passingValue = section.passingValueMax - value;

          this.onValueChange('passingValue', passingValue, { rigOperation, stage, section });
        }

        break;
      }

      case 'passingValueMax': {
        if (typeof value !== 'number' && value !== null) {
          console.error('invalid value');
          return;
        }

        section.setPassingValueMax(value);
        section.removeError('passingValueMax');

        if (!hasValue(value) || (hasValue(section.passingValueMin) && section.passingValueMin > value)) {
          break;
        }

        if (hasValue(section.passingValue) && !hasValue(section.passingValueMin)) {
          const passingValueMin = value - section.passingValue;

          this.onValueChange('passingValueMin', passingValueMin, { rigOperation, stage, section });
        } else if (hasValue(section.passingValueMin)) {
          const passingValue = value - section.passingValueMin;

          this.onValueChange('passingValue', passingValue, { rigOperation, stage, section });
        }

        break;
      }
    }

    this.eventBus.publish('rigOperationItemFieldValueChange', { fieldId, value, rigOperation, stage, section });
  }

  @computed
  get isSectionDetailingOrManyRigOperations(): boolean {
    if (this.rigOperations.length > 1) {
      return true;
    }

    return !!this.rigOperations[0]?.stages.some((stage) => stage.sections.length);
  }

  @flow.bound
  async *restoreRigOperationToDirectoryValues(rigOperation: RigOperation): Promise<void> {
    try {
      const planVersionId = this.rootStore.drafts.draft?.id;

      assert(hasValue(planVersionId), 'planVersionId is not presented');

      const serializedForm = this.formStore.getSerializedForm();
      const serializedRigOperation = this.formStore.serializeRigOperations([rigOperation]);

      serializedForm[RIG_OPERATIONS_KEY] = serializedRigOperation;

      const [updatedSerializedRigOperation] = await getAutocompletedRigOperations(serializedForm, planVersionId);

      const deserializedRigOperationData = this.deserializeRigOperation(updatedSerializedRigOperation);

      if (!deserializedRigOperationData) {
        console.error('serializedRigOperations is wrong');
        return;
      }

      this.updateRigOperationData(rigOperation, deserializedRigOperationData);
    } catch (e) {
      yield;

      if ((e instanceof Validation400ApiError || e instanceof ValidationError) && e.message) {
        this.notificationsService.showErrorMessage(e.message);
        return;
      } else if (e instanceof BaseApiError && e.responseMessage) {
        this.notificationsService.showErrorMessage(e.responseMessage);
        return;
      } else {
        this.notificationsService.showErrorMessageT('newWellForm:Errors.createWell');
      }

      console.error(e);
    }
  }

  @action.bound
  addRigOperation(): void {
    const index = this.rigOperations.length;

    const newRigOperation = new RigOperation(index);

    this.rigOperations = [...this.rigOperations, newRigOperation];
    this.rigOperationsMap.set(newRigOperation.entityId, newRigOperation);

    this.eventBus.publish('newRigOperation', { rigOperation: newRigOperation, index });
  }

  @action.bound
  addStage(rigOperation: RigOperation, initialStageValues?: Partial<TRawRigOperationStage>): void {
    const index = rigOperation.stages.length;

    const startDate = (() => {
      const prevStage = rigOperation.stages[rigOperation.stages.length - 1];

      if (prevStage) {
        const lastSection = prevStage.sections[prevStage.sections.length - 1];

        if (lastSection) {
          return lastSection.endDate;
        }

        return prevStage.endDate;
      }
    })();

    const newStage = new RigOperationStage(index, { startDate: startDate ?? undefined, ...initialStageValues });

    rigOperation.setStages([...rigOperation.stages, newStage]);
    this.eventBus.publish('newStage', { rigOperation, stage: newStage });
  }

  @action.bound
  addSection(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    initialSectionValues?: Partial<TRawRigOperationSection>,
  ): void {
    const index = stage.sections.length;

    const startDate = (() => {
      const prevSection = stage.sections[stage.sections.length - 1];

      if (prevSection) {
        return prevSection.endDate;
      }

      if (index !== 0) {
        return;
      }

      if (stage.startDate) {
        return stage.startDate;
      }

      const prevStage = rigOperation.stages[rigOperation.stages.indexOf(stage) - 1];

      if (prevStage) {
        const lastSection = prevStage.sections[prevStage.sections.length - 1];

        if (lastSection) {
          return lastSection.endDate;
        }

        return prevStage.endDate;
      }
    })();

    const newSection = new RigOperationSection(index, { startDate: startDate ?? undefined, ...initialSectionValues });

    stage.setSections([...stage.sections, newSection]);
    this.eventBus.publish('newSection', { rigOperation, stage, section: newSection });
  }

  @action.bound
  setRigOperations(rigOperations: RigOperation[]): void {
    if (equalsDeep(rigOperations, this.rigOperations)) {
      return;
    }

    this.rigOperations = rigOperations;
    this.rigOperationsMap.clear();
    rigOperations.forEach((ro) => {
      this.rigOperationsMap.set(ro.entityId, ro);
    });

    this.eventBus.publish('updatedRigOperaionList', rigOperations);
  }

  @action.bound
  setStages(rigOperation: RigOperation, stages: RigOperationStage[]): void {
    if (equalsDeep(rigOperation.stages, stages)) {
      return;
    }

    rigOperation.setStages(stages);

    this.eventBus.publish('updatedStageList', { rigOperation, stages });
  }

  @action.bound
  setSections(rigOperation: RigOperation, stage: RigOperationStage, sections: RigOperationSection[]): void {
    if (equalsDeep(stage.sections, sections)) {
      return;
    }

    stage.setSections(sections);

    this.eventBus.publish('updatedSectionsList', { rigOperation, stage, sections });
  }

  @action.bound
  deleteRigOperation(rigOperation: RigOperation): void {
    const index = this.rigOperations.indexOf(rigOperation);

    if (index < 0) {
      console.error('cannot find rigOperation', rigOperation);
      return;
    }

    const copiedRigOperations = [...this.rigOperations];
    copiedRigOperations.splice(index, 1);
    this.rigOperationsMap.delete(rigOperation.entityId);
    this.rigOperations = copiedRigOperations;

    this.eventBus.publish('deleteRigOperation', rigOperation);
  }

  @action.bound
  deleteStage(rigOperation: RigOperation, stage: RigOperationStage): void {
    const index = rigOperation.stages.indexOf(stage);

    if (index < 0) {
      console.error('cannot find stage', stage);
      return;
    }

    const copiedStages = [...rigOperation.stages];
    copiedStages.splice(index, 1);

    rigOperation.setStages(copiedStages);

    this.eventBus.publish('deleteStage', { deletedItemIndex: index, rigOperation, stage });
  }

  @action.bound
  deleteSection(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): void {
    const index = stage.sections.indexOf(section);

    if (index < 0) {
      console.error('cannot find section', section);
      return;
    }

    const copiedSections = [...stage.sections];
    copiedSections.splice(index, 1);

    stage.setSections(copiedSections);

    this.eventBus.publish('deleteSection', { deletedItemIndex: index, rigOperation, stage, section });
  }

  @action.bound
  onValueChange(fieldId: string, value: unknown, { rigOperation, stage, section }: TValueChangePayload): void {
    if (section) {
      if (!stage) {
        console.error('stage is not presented');
        return;
      }

      this.onSectionFieldValueChange(fieldId, value, rigOperation, stage, section);
      return;
    }

    if (stage) {
      this.onStageFieldValueChange(fieldId, value, rigOperation, stage);
      return;
    }

    this.onRigOperationFieldValueChange(fieldId, value, rigOperation);
  }

  @flow.bound
  async *setDirectoryValue(
    fieldId: string,
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ) {
    const directoryValue = await (async () => {
      switch (fieldId) {
        case 'duration': {
          await Promise.all([
            this.directoryValuesService.loadCommercialSpeedDirectory(rigOperation, stage),
            this.directoryValuesService.loadWellPassingValueDirectory(),
          ]);

          return this.directoryValuesService.getDirStageDuration(rigOperation, stage);
        }
        case 'passingValue': {
          if (!stage.startDate) {
            return null;
          }

          return this.directoryValuesService.getDirPassingValue(stage.startDate, true);
        }
        case 'commercialSpeed': {
          await this.directoryValuesService.loadCommercialSpeedDirectory(rigOperation, stage);

          return this.directoryValuesService.getDirStageCommercialSpeed(rigOperation, stage, true);
        }
      }
    })();
    yield;

    if (!hasValue(directoryValue)) {
      console.error('cannot find directory value');
      return;
    }

    this.onValueChange(fieldId, directoryValue, { rigOperation, stage, section });
  }

  @action.bound
  moveStage(rigOperation: RigOperation, from: number, to: number): void {
    if (from === to) {
      return;
    }

    const activeStage = rigOperation.stages[from];
    const targetStage = rigOperation.stages[to];

    if (!activeStage || !targetStage) {
      console.error('cannot find stages');
      return;
    }

    const newFirstStageStartDate = (() => {
      if ((from !== 0 && to !== 0) || (!activeStage.startDate && !targetStage.startDate)) {
        return null;
      }

      if (activeStage.startDate && targetStage.startDate) {
        return Math.min(activeStage.startDate, targetStage.startDate);
      }

      return activeStage.startDate ?? targetStage.startDate;
    })();

    const copiedStages = [...rigOperation.stages];
    copiedStages.splice(from, 1);

    copiedStages.splice(to, 0, activeStage);

    this.setStages(rigOperation, copiedStages);

    if (newFirstStageStartDate) {
      const firstStage = rigOperation.stages[0];
      const firstSection = firstStage.sections[0];

      this.onValueChange('startDate', newFirstStageStartDate, {
        rigOperation,
        stage: firstStage,
        section: firstSection,
      });
    } else {
      this.recalculateDateIntervals();
    }
  }

  @action.bound
  moveSection(rigOperation: RigOperation, stage: RigOperationStage, from: number, to: number): void {
    if (from === to) {
      return;
    }

    const activeSection = stage.sections[from];
    const targetSection = stage.sections[to];

    if (!activeSection || !targetSection) {
      console.error('cannot find sections');
      return;
    }

    const isFirstStage = rigOperation.stages.indexOf(stage) === 0;

    const newFirstSectionStartDate = (() => {
      if ((from !== 0 && to !== 0) || !isFirstStage || (!activeSection.startDate && !targetSection.startDate)) {
        return null;
      }

      if (activeSection.startDate && targetSection.startDate) {
        return Math.min(activeSection.startDate, targetSection.startDate);
      }

      return activeSection.startDate ?? targetSection.startDate;
    })();

    const copiedSections = [...stage.sections];
    copiedSections.splice(from, 1);

    copiedSections.splice(to, 0, activeSection);

    this.setSections(rigOperation, stage, copiedSections);

    if (newFirstSectionStartDate) {
      const firstSection = stage.sections[0];

      this.onValueChange('startDate', newFirstSectionStartDate, {
        rigOperation,
        stage,
        section: firstSection,
      });
    } else {
      this.recalculateDateIntervals();
    }
  }

  isDrillingStage = computedFn((stageTypeId: number | null): boolean => {
    if (!hasValue(stageTypeId)) {
      return false;
    }

    const directory = this.directories.getObject('GOplan_Dict_WellLifeCycleStage');

    if (!directory) {
      console.error('GOplan_Dict_WellLifeCycleStage directory is not presented');
      return false;
    }

    return directory.find((dv) => dv.id === stageTypeId)?.data['slug'] === 'drilling';
  });

  hasSections = computedFn((stageTypeId: number | null): boolean => {
    if (!hasValue(stageTypeId)) {
      return false;
    }

    const directory = this.directories.getObject('GOplan_Dict_WellLifeCycleStage');

    if (!directory) {
      console.error('GOplan_Dict_WellLifeCycleStage directory is not presented');
      return false;
    }

    return !!directory.find((dv) => dv.id === stageTypeId)?.data['hasSections'];
  });

  hasDistance = computedFn((stageTypeId: number | null): boolean => {
    if (!hasValue(stageTypeId)) {
      return false;
    }

    const directory = this.directories.getObject('GOplan_Dict_WellLifeCycleStage');

    if (!directory) {
      console.error('GOplan_Dict_WellLifeCycleStage directory is not presented');
      return false;
    }

    return !!directory.find((dv) => dv.id === stageTypeId)?.data['hasDistance'];
  });

  private calculateCommercialSpeed({
    rigOperation,
    stage,
  }: {
    rigOperation: RigOperation;
    stage?: RigOperationStage;
  }): void {
    if (!stage) {
      console.error('stage is not presented');
      return;
    }
    // TODO: вместо 30.4 добавить получение значения из справочника

    const { passingValue, duration } = stage;

    if (this.rigOperations.length < 2) {
      if (hasValue(stage.commercialSpeed)) {
        this.onValueChange('commercialSpeed', null, { rigOperation, stage });
      }

      return;
    }

    if (!hasValue(passingValue) || !hasValue(duration) || passingValue <= 0 || duration <= 0) {
      return;
    }

    const commercialSpeed = calculateCommercialSpeed(passingValue, duration);

    if (commercialSpeed === stage.commercialSpeed) {
      return;
    }

    this.onValueChange('commercialSpeed', commercialSpeed, { rigOperation, stage });
  }

  private calculateStageDurationByCommercialSpeed({
    rigOperation,
    stage,
  }: {
    rigOperation: RigOperation;
    stage?: RigOperationStage;
  }): void {
    if (!stage) {
      console.error('stage is not presented');
      return;
    }

    const { passingValue, commercialSpeed } = stage;

    if (
      !hasValue(passingValue) ||
      !hasValue(commercialSpeed) ||
      passingValue <= 0 ||
      commercialSpeed <= 0 ||
      this.rigOperations.length < 2
    ) {
      return;
    }

    const duration = calculateDurationByCommercialSpeed(passingValue, commercialSpeed);

    if (duration === stage.duration) {
      return;
    }

    this.onValueChange('duration', duration, { rigOperation, stage });
  }

  private calculateStageIntervalAndDurationBySections({
    rigOperation,
    stage,
  }: {
    rigOperation: RigOperation;
    stage?: RigOperationStage;
  }): void {
    if (!stage) {
      console.error('stage is not presented');
      return;
    }

    const startDateBySection = stage.sections[0]?.startDate;
    const endDateBySection = stage.sections.filter((s) => s.endDate).reverse()[0]?.endDate;

    if (startDateBySection) {
      this.onValueChange('startDate', startDateBySection, { rigOperation, stage });
    }

    if (endDateBySection) {
      this.onValueChange('endDate', endDateBySection, { rigOperation, stage });
    }
  }

  private calculateStagePassingValueBySections({
    rigOperation,
    stage,
  }: {
    rigOperation: RigOperation;
    stage?: RigOperationStage;
  }): void {
    if (!stage) {
      console.error('stage is not presented');
      return;
    }

    const stagePassingValue = stage.sections.reduce((acc: number | null, currSection) => {
      if (hasValue(currSection.passingValue)) {
        if (currSection.passingValue < 0) {
          return acc;
        }

        if (acc === null) {
          acc = currSection.passingValue;
        } else {
          acc = acc + currSection.passingValue;
        }
      }

      return acc;
    }, null);

    this.onValueChange('passingValue', stagePassingValue, { rigOperation, stage });
  }

  @action.bound
  private recalculateDateIntervals(rigOperation?: RigOperation): void {
    const startRigOperation = rigOperation ?? this.rigOperations[0] ?? null;

    const firstStage = this.rigOperations[0]?.stages[0];

    const startStage = firstStage ?? null;
    const startSection = firstStage?.sections[0] ?? null;

    if (!startRigOperation) {
      return;
    }

    const calcultaInterval = (
      currRigOperation: RigOperation,
      currStage: RigOperationStage | null,
      currentSection: RigOperationSection | null,
    ): void => {
      const currentRigOperationIndex = this.rigOperations.indexOf(currRigOperation);
      const currentStageIndex = currStage ? currRigOperation.stages.indexOf(currStage) : null;
      const currentSectionIndex = currentSection && currStage ? currStage.sections.indexOf(currentSection) : null;

      const currentNode = currentSection ?? currStage;

      const { nextNodeRigOperation, nextNodeStage, nextNodeSection } = ((): {
        nextNodeRigOperation: RigOperation | null;
        nextNodeStage: RigOperationStage | null;
        nextNodeSection: RigOperationSection | null;
      } => {
        let nextNodeRigOperation: RigOperation | null = currRigOperation;
        let nextNodeStage: RigOperationStage | null = currStage ?? null;
        const nextNodeSection: RigOperationSection | null = (() => {
          if (!hasValue(currentSectionIndex) || currentSectionIndex < 0) {
            return null;
          }

          return currStage?.sections[currentSectionIndex + 1] ?? null;
        })();

        if (nextNodeSection) {
          return { nextNodeRigOperation, nextNodeStage, nextNodeSection };
        }

        nextNodeStage = (() => {
          if (!hasValue(currentStageIndex) || currentStageIndex < 0) {
            return null;
          }

          return currRigOperation?.stages[currentStageIndex + 1] ?? null;
        })();

        if (nextNodeStage) {
          return { nextNodeRigOperation, nextNodeStage, nextNodeSection: nextNodeStage.sections[0] ?? null };
        }

        nextNodeRigOperation = (() => {
          if (!hasValue(currentRigOperationIndex) || currentRigOperationIndex < 0) {
            return null;
          }

          return (
            this.rigOperations.slice(currentRigOperationIndex + 1).find((rigOperation) => rigOperation.stages.length) ??
            null
          );
        })();

        if (nextNodeRigOperation) {
          const firstStage = nextNodeRigOperation.stages[0];

          return {
            nextNodeRigOperation,
            nextNodeStage: firstStage ?? null,
            nextNodeSection: firstStage?.sections[0] ?? null,
          };
        }

        return { nextNodeRigOperation: null, nextNodeStage: null, nextNodeSection: null };
      })();

      if (!nextNodeRigOperation || (!nextNodeStage && !nextNodeSection)) {
        return;
      }

      const targetSource = nextNodeSection ?? nextNodeStage;

      if (currentNode && targetSource) {
        if (currentNode.endDate !== targetSource.startDate) {
          if (
            nextNodeRigOperation === currRigOperation ||
            (currentNode.endDate && targetSource.startDate && currentNode.endDate > targetSource.startDate)
          ) {
            if (targetSource instanceof RigOperationSection) {
              this.onValueChange('startDate', currentNode.endDate, {
                rigOperation: nextNodeRigOperation,
                stage: nextNodeStage!,
                section: nextNodeSection!,
              });
            } else {
              this.onValueChange('startDate', currentNode.endDate, {
                rigOperation: nextNodeRigOperation,
                stage: nextNodeStage!,
              });
            }
          }
        }
      }

      calcultaInterval(nextNodeRigOperation, nextNodeStage, nextNodeSection);
    };

    calcultaInterval(startRigOperation, startStage, startSection);
  }

  init = (): VoidFunction => {
    if (this.initialValues) {
      const initialRigOperations = this.initialValues[RIG_OPERATIONS_KEY];
      this.initializeRigOperations(initialRigOperations);
    }

    const stageDurationCalculationsDisposer = (() => {
      const sectionDurationDisposer = this.eventBus.subscribe('rigOperationItemFieldValueChange', (params) => {
        const { fieldId, value, rigOperation, stage, section } = params;

        if (!['startDate', 'endDate'].includes(fieldId) || !section) {
          return;
        }

        if (!hasValue(value)) {
          return;
        }

        if (typeof value !== 'number') {
          console.error('duration has wrong type', value);
          return;
        }

        this.calculateStageIntervalAndDurationBySections({ rigOperation, stage });
      });
      const sectionDeletionDisposer = this.eventBus.subscribe(
        'deleteSection',
        this.calculateStageIntervalAndDurationBySections.bind(this),
      );
      const sectionsUpdateDisposer = this.eventBus.subscribe(
        'updatedSectionsList',
        this.calculateStageIntervalAndDurationBySections.bind(this),
      );

      return () => {
        sectionDurationDisposer();
        sectionDeletionDisposer();
        sectionsUpdateDisposer();
      };
    })();

    const stagePassingValueCalculationsDisposer = (() => {
      const sectionPassingValueDisposer = this.eventBus.subscribe('rigOperationItemFieldValueChange', (params) => {
        const { fieldId, value, rigOperation, stage, section } = params;

        if (fieldId !== 'passingValue' || !section) {
          return;
        }

        if (!hasValue(value)) {
          return;
        }

        if (typeof value !== 'number') {
          console.error('passingValue has wrong type', value);
          return;
        }

        this.calculateStagePassingValueBySections({ rigOperation, stage });
      });
      const sectionDeletionDisposer = this.eventBus.subscribe(
        'deleteSection',
        this.calculateStagePassingValueBySections.bind(this),
      );
      const sectionsUpdateDisposer = this.eventBus.subscribe(
        'updatedSectionsList',
        this.calculateStagePassingValueBySections.bind(this),
      );

      return () => {
        sectionPassingValueDisposer();
        sectionDeletionDisposer();
        sectionsUpdateDisposer();
      };
    })();

    const commercialSpeedDisposer = ((): VoidFunction => {
      const stagePassingValueDisposer = this.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        ({ fieldId, rigOperation, stage, section }) => {
          if (fieldId !== 'passingValue' || section) {
            return;
          }

          this.calculateCommercialSpeed({ rigOperation, stage });
        },
      );
      const stageDurationDisposer = this.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        ({ fieldId, rigOperation, stage, section }) => {
          if (fieldId !== 'duration' || section || !this.isDrillingStage(stage?.wellLifeCycleStageId ?? null)) {
            return;
          }

          this.calculateCommercialSpeed({ rigOperation, stage });
        },
      );
      const deleteRigOperationDisposer = this.eventBus.subscribe('deleteRigOperation', () => {
        if (this.rigOperations.length > 1) {
          return;
        }

        const rigOperation = this.rigOperations[0];

        if (!rigOperation) {
          return;
        }

        const stage = rigOperation.stages.find((s) => this.isDrillingStage(s.wellLifeCycleStageId));

        if (!stage || !hasValue(stage.commercialSpeed)) {
          return;
        }

        this.onValueChange('commercialSpeed', null, { rigOperation, stage });
      });
      const newRigOperationDisposer = this.eventBus.subscribe('newRigOperation', () => {
        if (this.rigOperations.length < 2) {
          return;
        }

        const firstRigOperation = this.rigOperations[0];
        const drillingStage = firstRigOperation.stages.find((s) => this.isDrillingStage(s.wellLifeCycleStageId));

        if (!drillingStage) {
          return;
        }

        this.calculateCommercialSpeed({ rigOperation: firstRigOperation, stage: drillingStage });
      });

      return () => {
        stagePassingValueDisposer();
        stageDurationDisposer();
        deleteRigOperationDisposer();
        newRigOperationDisposer();
      };
    })();
    const calculateDurationByCommercialSpeedDisposer = this.eventBus.subscribe(
      'rigOperationItemFieldValueChange',
      ({ fieldId, rigOperation, stage, section }) => {
        if (!['commercialSpeed', 'passingValue'].includes(fieldId) || section) {
          return;
        }

        this.calculateStageDurationByCommercialSpeed({ rigOperation, stage });
      },
    );

    const recalculateDateIntervalsDisposer = (() => {
      const endDateChangeDisposer = this.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        ({ fieldId, rigOperation, stage, section }) => {
          if (!['duration', 'startDate'].includes(fieldId)) {
            return;
          }

          // Игнорируем такие этапы, т.к. их интервалы выстраиваются в зависимости от интервалов секций
          if (!stage || (!section && stage?.sections.length)) {
            return;
          }

          switch (fieldId) {
            case 'duration': {
              this.recalculateDateIntervals();
              break;
            }
            case 'startDate': {
              const isFirstStage = rigOperation.stages.indexOf(stage) === 0;

              if (!isFirstStage) {
                return;
              }

              if (!stage.sections.length) {
                this.recalculateDateIntervals();
                return;
              }

              if (!section) {
                return;
              }

              const isFirstSection = stage.sections.indexOf(section) === 0;

              if (!isFirstSection) {
                return;
              }

              this.recalculateDateIntervals();
            }
          }
        },
      );

      const deleteSectionDisposer = this.eventBus.subscribe('deleteSection', (params) => {
        this.recalculateDateIntervals();
      });
      const deleteStageDisposer = this.eventBus.subscribe('deleteStage', (params) => {
        this.recalculateDateIntervals();
      });

      return () => {
        endDateChangeDisposer();
        deleteSectionDisposer();
        deleteStageDisposer();
      };
    })();

    return () => {
      stageDurationCalculationsDisposer();
      stagePassingValueCalculationsDisposer();
      commercialSpeedDisposer();
      calculateDurationByCommercialSpeedDisposer();
      recalculateDateIntervalsDisposer();
    };
  };
}

declare module '../form.entity' {
  export interface IFormEvents {
    newRigOperation: {
      rigOperation: RigOperation;
      index: number;
    };
    newStage: {
      rigOperation: RigOperation;
      stage: RigOperationStage;
    };
    newSection: {
      rigOperation: RigOperation;
      stage: RigOperationStage;
      section: RigOperationSection;
    };
    deleteRigOperation: RigOperation;
    deleteStage: {
      deletedItemIndex: number;
      rigOperation: RigOperation;
      stage: RigOperationStage;
    };
    deleteSection: {
      deletedItemIndex: number;
      rigOperation: RigOperation;
      stage: RigOperationStage;
      section: RigOperationSection;
    };
    updatedRigOperaionList: RigOperation[];
    updatedStageList: {
      rigOperation: RigOperation;
      stages: RigOperationStage[];
    };
    updatedSectionsList: {
      rigOperation: RigOperation;
      stage: RigOperationStage;
      sections: RigOperationSection[];
    };
    initializedRigOperaionList: RigOperation[];
    initializedStageList: {
      rigOperation: RigOperation;
      stages: RigOperationStage[];
    };
    initializedSectionsList: {
      rigOperation: RigOperation;
      stage: RigOperationStage;
      sections: RigOperationSection[];
    };
    rigOperationItemFieldValueChange: {
      fieldId: string;
      value: unknown;
      rigOperation: RigOperation;
      stage?: RigOperationStage;
      section?: RigOperationSection;
    };
  }
}
